import { Box, CircularProgress, Select, Option, Stack, Typography } from "@mui/joy";
import { format } from "date-fns";
import {
  ApiCompetition,
  ApiCompetitionDetail,
  ApiCompetitionEntryInline,
  ApiRouteAttempt,
  ClimbType,
} from "griptonite-api-client";
import { useEffect, useMemo, useRef, useState } from "react";

import { Route } from "@/core/types";
import { useCompetitionEntriesByUuid } from "@/hooks/competitions";
import { useRouteAttemptsByUuidGrouped } from "@/hooks/routes";
import formatNumber from "@/utils/formatNumber";

import Avatar from "../Avatar";
import { useCompetitionContext } from "../CompetitionOverview/CompetitionProvider";
import { StackLink } from "../Links";
import { AttemptsPreview } from "./AttemptsPreview";

const AttemptsGroup = ({
  competition,
  route,
  attempts,
  entry,
  climbType,
}: {
  competition?: ApiCompetition & Partial<ApiCompetitionDetail>;
  route?: Route;
  attempts: ApiRouteAttempt[];
  entry: ApiCompetitionEntryInline;
  climbType: ClimbType;
}) => {
  // score is the last non-zero attempt score
  const score = [...attempts].reverse().find((a) => a.score && a.score > 0)?.score || 0;
  const timestamp = attempts[attempts.length - 1].timestamp;
  return (
    <StackLink
      from="/gym/$venueUuid/events/$competitionUuid/routes/$routeUuid"
      to="/gym/$venueUuid/events/$competitionUuid/competitors/$userUuid"
      params={{
        userUuid: entry.user.uuid,
      }}
      sx={{
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <Stack direction="row" gap={0.5} alignItems="center">
        <Avatar user={entry.user} size={24} />
        <Typography level="body-sm" fontWeight="bold" noWrap flex={1}>
          {entry.user.name}
        </Typography>
        <AttemptsPreview
          attempts={attempts}
          climbType={climbType}
          segmentsCount={route?.segmentsCount}
        />
      </Stack>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Typography level="body-sm" textColor="neutral.600" flex={1}>
          {format(timestamp, "do MMM, HH:mm")}
        </Typography>
        {competition?.scoringSystem !== "IFSC_RANKING" && score ? (
          <Typography level="body-sm" fontWeight="bold">
            {formatNumber(score, {
              maxDecimals: competition?.scoringSystem === "IFSC_SPORT" ? 2 : 1,
            })}{" "}
            pts
          </Typography>
        ) : null}
      </Stack>
    </StackLink>
  );
};

export const CompetitionRouteAttempts = ({
  route,
  competition,
}: {
  route?: Route;
  competition?: ApiCompetition & Partial<ApiCompetitionDetail>;
}) => {
  const attemptsQuery = useRouteAttemptsByUuidGrouped(route?.uuid, true);
  const entriesQuery = useCompetitionEntriesByUuid(competition?.uuid);

  const { selectedCategoryUuid: pageSelectedCategoryUuid } = useCompetitionContext();

  const [selectedCategoryUuid, setSelectedCategoryUuid] = useState<string | null>(
    pageSelectedCategoryUuid,
  );

  useEffect(() => {
    setSelectedCategoryUuid(pageSelectedCategoryUuid);
  }, [pageSelectedCategoryUuid]);

  const attemptsByUser = attemptsQuery.data;
  const entriesByUuid = entriesQuery.data;

  const routeCategories = useMemo(() => {
    if (!route) {
      return new Set();
    }
    return new Set(route?.competitionRouteGroups.flatMap((x) => x.categories));
  }, [route]);

  const selectedCategoryUuidRef = useRef(selectedCategoryUuid);
  useEffect(() => {
    selectedCategoryUuidRef.current = selectedCategoryUuid;
  }, [selectedCategoryUuid]);

  useEffect(() => {
    if (selectedCategoryUuidRef.current && !routeCategories.has(selectedCategoryUuidRef.current)) {
      setSelectedCategoryUuid(null);
    }
  }, [routeCategories, setSelectedCategoryUuid]);

  const filteredAttemptGroups = useMemo(() => {
    if (!attemptsByUser || !entriesByUuid) {
      return [];
    }
    if (!selectedCategoryUuid || !routeCategories.has(selectedCategoryUuid)) {
      return Object.values(attemptsByUser);
    }

    const filteredUserUuids = new Set(
      Object.values(entriesByUuid)
        .filter((x) => x.category === selectedCategoryUuid)
        .map((x) => x.user.uuid),
    );

    const filteredAttemptGroups = Object.values(attemptsByUser).filter((x) =>
      filteredUserUuids.has(x[0].user),
    );
    return filteredAttemptGroups;
  }, [attemptsByUser, entriesByUuid, selectedCategoryUuid, routeCategories]);

  if (attemptsQuery.isPending || entriesQuery.isPending || !competition) {
    return <CircularProgress />;
  }

  if (attemptsQuery.isError || entriesQuery.isError) {
    console.error(attemptsQuery.error);
    console.error(entriesQuery.error);
    return <Box>Error</Box>;
  }

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Box sx={{ padding: 1 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography level="body-sm" fontWeight="bold">
            Category
          </Typography>
          <Select
            size="sm"
            value={routeCategories.has(selectedCategoryUuid) ? selectedCategoryUuid : "ALL"}
            onChange={(_e, value) =>
              value && setSelectedCategoryUuid(value === "ALL" ? null : value)
            }
          >
            <Option value="ALL">All</Option>
            {competition?.stages[0].categories
              .filter((c) => routeCategories.has(c.uuid))
              .map((category) => (
                <Option key={category.uuid} value={category.uuid}>
                  {category.name}
                </Option>
              ))}
          </Select>
        </Stack>
      </Box>
      <Stack
        sx={{
          // gap: 0.75,
          gap: 1,
          flex: 1,
          overflowY: "scroll",
          minHeight: 0,
          paddingX: 1,
        }}
      >
        {filteredAttemptGroups.map((attempts) => {
          const entry = entriesByUuid?.[attempts[0].user];
          if (!entry) {
            return null;
          }
          return (
            <AttemptsGroup
              key={entry.user.uuid}
              attempts={attempts}
              entry={entry}
              climbType={competition?.climbType}
              competition={competition}
              route={route}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};
